import { type OverlayLayerOption } from './type';

type PolicyLayer = Pick<
  OverlayLayerOption,
  | 'clickEnabled'
  | 'clickTrigger'
  | 'cursor'
  | 'enabled'
  | 'hitTest'
  | 'hoverEnabled'
  | 'onClick'
  | 'onHoverChange'
  | 'plane'
>;

/** 호버만 받는 레이어인지. 클릭 콜백 없이 호버 옵션만 쓰는 레이어는 클릭을 가로채지 않습니다. */
const isHoverOnly = (layer: PolicyLayer) =>
  (layer.hoverEnabled === true || layer.onHoverChange != null) &&
  layer.onClick == null &&
  layer.clickEnabled !== true;

/**
 * 호버에 참여하는 top 평면 레이어. 호버를 받을 수 있는 조건(`hoverEnabled`, 생략 시 `enabled`)을
 * 만족하고, 알림이나 커서를 쓰겠다고 밝힌 레이어입니다. 그리기 조건과는 별개입니다.
 */
export const isHoverParticipant = (layer: PolicyLayer) => {
  if ((layer.plane ?? 'top') !== 'top') return false;
  if (!(layer.hoverEnabled ?? layer.enabled)) return false;
  return (
    layer.hoverEnabled === true ||
    layer.onHoverChange != null ||
    layer.cursor != null
  );
};

/**
 * 클릭에 참여하는 레이어. `onClick` 이 있으면 참여하고, `hitTest` 만 있고 onClick 이 없던 기존 레이어는
 * 입력을 차단하는 호환 동작을 유지합니다. 호버 전용 레이어는 참여하지 않습니다.
 */
export const isClickParticipant = (layer: PolicyLayer) => {
  // 판정은 top 캔버스만 하므로 underLabel 평면은 클릭에 참여하지 않습니다.
  if ((layer.plane ?? 'top') !== 'top') return false;
  if (!(layer.clickEnabled ?? layer.enabled)) return false;
  if (layer.onClick != null) return true;
  return layer.hitTest != null && !isHoverOnly(layer);
};

/** pointermove/up/leave/cancel 리스너가 필요한 레이어(호버 참여 또는 release 클릭). */
export const isPointerParticipant = (layer: PolicyLayer) =>
  isHoverParticipant(layer) ||
  (layer.clickTrigger === 'release' && isClickParticipant(layer));
